import type { OrderEventData, PendingOrderType } from '../../bot/types/chartData'
import { PENDING_TYPE_LABEL, restingOrdersAt } from '../../bot/utils/pendingOrders'

/** One row of the trades pane's Pending tab: an order resting at the playhead. */
export interface PendingOrderRow {
  /** The engine's order ticket (the row key). */
  ticket: number
  /** The id the strategy code sees — what `buy_limit()` returned, what `cancel_order()` takes. */
  orderId: number
  orderType: PendingOrderType
  label: string
  side: 'BUY' | 'SELL'
  price: number
  volume: number
  sl: number | null
  tp: number | null
  placedTs: number
  /** Open time of the bar the order expires at, or null for good-till-cancelled. */
  expiresAt: number | null
  /** Whole bars left before expiry as of the playhead, or null for good-till-cancelled. */
  barsLeft: number | null
  /** Order price minus the last revealed close, in pips (positive = above the market). */
  distancePips: number | null
}

/**
 * The orders resting at the playhead, newest first. The same `restingOrdersAt` the chart's
 * dotted order lines use, so the tab and the canvas agree on what is working.
 */
export function buildPendingOrders(
  activity: OrderEventData[],
  playheadTs: number,
  markPrice: number | null,
  pipSize: number,
  tfSec: number,
): PendingOrderRow[] {
  const rows = restingOrdersAt(activity, playheadTs).map((a): PendingOrderRow => ({
    ticket: a.ticket,
    orderId: a.orderId,
    orderType: a.orderType,
    label: PENDING_TYPE_LABEL[a.orderType],
    side: a.side,
    price: a.price,
    volume: a.volume,
    sl: a.sl,
    tp: a.tp,
    placedTs: a.time,
    expiresAt: a.expiresAt,
    barsLeft:
      a.expiresAt == null || !(tfSec > 0)
        ? null
        : Math.max(0, Math.ceil((a.expiresAt - playheadTs) / tfSec)),
    distancePips:
      markPrice != null && Number.isFinite(pipSize) && pipSize > 0
        ? (a.price - markPrice) / pipSize
        : null,
  }))
  rows.sort((a, b) => b.placedTs - a.placedTs || b.ticket - a.ticket)
  return rows
}
